import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle, Clock, Shield, Cpu, History, Handshake, Newspaper, Phone, Mail, MapPin, ChevronRight, ExternalLink, Globe } from 'lucide-react';

// --- Components ---

const Section = ({ title, subtitle, children, id, className = "" }: { title: string; subtitle?: string; children: React.ReactNode; id?: string; className?: string }) => (
  <section id={id} className={`py-20 ${className}`}>
    <div className="container mx-auto px-4">
      <div className="mb-12">
        <h2 className="section-title uppercase tracking-tight">{title}</h2>
        {subtitle && <p className="section-subtitle">{subtitle}</p>}
        <div className="h-1 w-20 bg-accent mt-4"></div>
      </div>
      {children}
    </div>
  </section>
);

const Logo = ({ className = "text-primary" }: { className?: string }) => (
  <div className={`logo-text flex items-center gap-1 ${className}`}>
    <span className="tracking-widest">OGGUN</span>
    <span className="logo-accent font-black">ITS</span>
  </div>
);

// --- Main App ---

export default function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [news, setNews] = useState<any[]>([]);
  const [newsArchive, setNewsArchive] = useState<any[]>([]);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [isNewsModalOpen, setIsNewsModalOpen] = useState(false);

  useEffect(() => {
    // Cache-busting: agregar timestamp para evitar caché del navegador
    const cacheBuster = `?v=${Date.now()}`;
    
    fetch(`/data/news.json${cacheBuster}`)
      .then(res => res.json())
      .then(data => setNews(data))
      .catch(err => console.error("Error loading news:", err));
    
    fetch(`/data/news-archive.json${cacheBuster}`)
      .then(res => res.json())
      .then(data => setNewsArchive(data))
      .catch(err => console.error("Error loading news archive:", err));
  }, []);

  const nextNews = () => {
    setCurrentNewsIndex((prev) => (prev + 1) % news.length);
  };

  const prevNews = () => {
    setCurrentNewsIndex((prev) => (prev - 1 + news.length) % news.length);
  };

  const closeModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsNewsModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen font-sans selection:bg-accent selection:text-white">
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-20 flex items-center justify-between">
          <Logo />
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#home" className="nav-link">Inicio</a>
            <a href="#servicios" className="nav-link">Servicios</a>
            <a href="#trayectoria" className="nav-link">Trayectoria</a>
            <a href="#abonos" className="nav-link">Abonos</a>
            <a href="#noticias" className="nav-link">Noticias</a>
            <a href="#contacto" className="btn-primary py-2 px-4 text-sm">
              <MessageCircle size={18} /> Soporte Directo
            </a>
          </div>

          <button className="md:hidden text-primary" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 p-6 flex flex-col space-y-6 shadow-xl">
            <a href="#home" className="text-lg font-semibold text-primary" onClick={() => setIsMenuOpen(false)}>Inicio</a>
            <a href="#servicios" className="text-lg font-semibold text-primary" onClick={() => setIsMenuOpen(false)}>Servicios</a>
            <a href="#trayectoria" className="text-lg font-semibold text-primary" onClick={() => setIsMenuOpen(false)}>Trayectoria</a>
            <a href="#abonos" className="text-lg font-semibold text-primary" onClick={() => setIsMenuOpen(false)}>Abonos</a>
            <a href="#noticias" className="text-lg font-semibold text-primary" onClick={() => setIsMenuOpen(false)}>Noticias</a>
            <a href="#contacto" className="btn-primary justify-center" onClick={() => setIsMenuOpen(false)}>
              <MessageCircle size={20} /> Soporte Directo
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative pt-20 pb-32 overflow-hidden bg-primary text-white">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#06b6d4_1px,transparent_1px)] [background-size:40px_40px]"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/20 text-accent text-xs font-bold uppercase tracking-widest mb-6 border border-accent/30">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
              </span>
              Resolución en menos de 15 minutos
            </div>
            
            <h1 className="text-5xl md:text-7xl font-black mb-8 leading-[1.1] tracking-tight">
              35 años de experiencia.<br />
              <span className="text-accent">Soporte de Guante Blanco.</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              Departamento de IT externo para PyMEs en Buenos Aires. 
              Contacto directo con expertos, sin tickets, sin demoras, sin sorpresas.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a href="https://wa.me/541126552124" target="_blank" rel="noopener noreferrer" className="btn-primary text-lg px-8 py-4">
                <MessageCircle size={24} /> WhatsApp Directo
              </a>
              <a href="#servicios" className="px-8 py-4 rounded-md font-semibold border border-slate-700 hover:bg-slate-800 transition-all flex items-center gap-2">
                Explorar Soluciones <ChevronRight size={20} />
              </a>
            </div>
          </div>
          
          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 pt-12 border-t border-slate-800">
            <div className="group">
              <div className="text-4xl md:text-5xl font-black text-accent mb-2 group-hover:scale-110 transition-transform duration-300 origin-left">35+</div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-400">Años de Trayectoria</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-black text-accent mb-2 group-hover:scale-110 transition-transform duration-300 origin-left">15m</div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-400">Respuesta Máxima</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-black text-accent mb-2 group-hover:scale-110 transition-transform duration-300 origin-left">99.9%</div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-400">Uptime Garantizado</div>
            </div>
            <div className="group">
              <div className="text-4xl md:text-5xl font-black text-accent mb-2 group-hover:scale-110 transition-transform duration-300 origin-left">+25</div>
              <div className="text-xs uppercase font-bold tracking-widest text-slate-400">Clientes Activos</div>
            </div>
          </div>
        </div>
      </section>

      {/* Servicios Section */}
      <Section 
        title="Soluciones Tecnológicas" 
        subtitle="Infraestructura robusta y conectividad de última generación diseñada para la continuidad de su negocio."
        id="servicios"
      >
        <div className="grid md:grid-cols-3 gap-8">
          <div className="card group hover:border-accent/50">
            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mb-6 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
              <Cpu size={32} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-primary">Infraestructura & Cloud</h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Despliegue de servidores Proxmox, Cloud Híbrida y Housing en CABA. 
              Soberanía de datos y escalabilidad real para su empresa.
            </p>
            <ul className="space-y-3">
              {['Virtualización Proxmox', 'Housing en Datacenters CABA', 'Cloud Híbrida (Local/Intl)'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div> {item}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="card group hover:border-accent/50">
            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mb-6 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
              <Shield size={32} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-primary">Conectividad Avanzada</h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Arquitecturas de red robustas y seguras. VPN de baja latencia con WireGuard, 
              ZeroTier y Netbird para un teletrabajo fluido y protegido.
            </p>
            <ul className="space-y-3">
              {['Redes de Alta Disponibilidad', 'VPN WireGuard / Netbird', 'SD-WAN y Enlaces de Fibra'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div> {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="card group hover:border-accent/50">
            <div className="w-14 h-14 bg-slate-100 rounded-lg flex items-center justify-center mb-6 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300">
              <Clock size={32} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-primary">Seguridad & Backup</h3>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Estrategia de Backup 3-2-1. Monitoreo preventivo para garantizar 
              "cero sorpresas" y la máxima continuidad operativa.
            </p>
            <ul className="space-y-3">
              {['Backup 3-2-1 Automatizado', 'Auditoría de Seguridad', 'Monitoreo 24/7 Preventivo'].map((item) => (
                <li key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <div className="w-1.5 h-1.5 rounded-full bg-accent"></div> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Trayectoria Section */}
      <section id="trayectoria" className="py-24 bg-slate-900 text-white overflow-hidden relative">
        {/* Background Graphic Element */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-full" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #06b6d4 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-slate-800/50 border border-slate-700 flex items-center justify-center overflow-hidden group relative">
                {/* Visual representation of evolution */}
                <div className="absolute inset-0 flex flex-col">
                  <div className="flex-1 relative overflow-hidden">
                    <img 
                      src="/images/past.png" 
                      alt="Pasado - Postes telefónicos" 
                      className="w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 transition-all duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 to-transparent"></div>
                  </div>
                  <div className="flex-1 relative overflow-hidden">
                    <img 
                      src="/images/present.png" 
                      alt="Presente - Datacenter" 
                      className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-all duration-1000"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                  </div>
                </div>
                
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-full h-px bg-accent/30 scale-x-150 rotate-12"></div>
                  <div className="w-full h-px bg-accent/30 scale-x-150 -rotate-12"></div>
                </div>
                
                <div className="absolute bottom-8 left-8 right-8 bg-primary/90 backdrop-blur-xl p-8 rounded-2xl border border-slate-700 shadow-2xl z-20">
                  <div className="text-accent font-black text-4xl mb-2">35 AÑOS</div>
                  <div className="text-xs uppercase font-black tracking-[0.3em] text-slate-400">De evolución tecnológica</div>
                </div>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent/20 rounded-full blur-[80px]"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-500/10 rounded-full blur-[80px]"></div>
            </div>
            
            <div>
              <div className="inline-block px-4 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-black tracking-widest uppercase mb-6">
                Nuestra Historia
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-8 uppercase tracking-tight leading-tight">
                De los 2400 baudios <br/>
                <span className="text-accent">a Gigabps por Fibra</span>
              </h2>
              
              <div className="space-y-12 relative">
                {/* Vertical Timeline Line */}
                <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-accent via-slate-700 to-transparent"></div>
                
                {[
                  { 
                    year: '1990s', 
                    title: 'La Era de la Optimización',
                    desc: 'Programación y Análisis de Sistemas en entornos donde cada byte era crítico. Módems de 2400 baudios y recursos extremadamente limitados.' 
                  },
                  { 
                    year: '2000s', 
                    title: 'Consolidación Estratégica',
                    desc: 'Independencia profesional y nacimiento de OGGUN ITS. Nos convertimos en el socio tecnológico de confianza para PyMEs en CABA.' 
                  },
                  { 
                    year: 'HOY', 
                    title: 'Soluciones de Próxima Generación',
                    desc: 'Expertos en arquitecturas híbridas, enlaces seguros y conectividad de Gigabps. Implementamos la solución técnica óptima para cada proyecto —ya sea Cloud, Baremetal o Redes Privadas— combinando las herramientas más avanzadas del mercado con la experiencia acumulada durante tres décadas de evolución constante.' 
                  }
                ].map((milestone, idx) => (
                  <div key={milestone.year} className="flex gap-8 group relative">
                    <div className="relative z-10">
                      <div className="w-6 h-6 rounded-full bg-slate-900 border-2 border-accent flex items-center justify-center group-hover:bg-accent transition-colors duration-300">
                        <div className="w-2 h-2 rounded-full bg-accent group-hover:bg-white"></div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-black text-accent mb-1 tracking-widest">{milestone.year}</div>
                      <h4 className="text-xl font-bold mb-2 text-white">{milestone.title}</h4>
                      <p className="text-slate-400 leading-relaxed text-sm md:text-base">{milestone.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Abonos Section */}
      <Section 
        title="El Método OGGUN: Abonos a Medida" 
        subtitle="No creemos en paquetes rígidos. Diseñamos cada servicio buscando el equilibrio justo entre su infraestructura y su presupuesto."
        id="abonos"
        className="bg-white"
      >
        <div className="max-w-5xl mx-auto">
          <div className="bg-slate-50 rounded-3xl p-8 md:p-16 border border-slate-200 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-5">
              <Handshake size={200} />
            </div>
            
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row gap-12">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-6 text-primary flex items-center gap-3">
                    <Handshake className="text-accent" /> Filosofía Win-Win
                  </h3>
                  <p className="text-slate-600 mb-8 leading-relaxed">
                    Cada cliente es un proyecto único. Analizamos su parque informático, sus procesos críticos y sus objetivos de crecimiento para proponer un abono mensual que garantice operatividad total sin costos innecesarios.
                  </p>
                  <div className="space-y-4">
                    {[
                      'Respuesta garantizada en < 15 minutos',
                      'Contacto directo con técnicos senior',
                      'Mantenimiento preventivo y proactivo',
                      'Transparencia total: Cero sorpresas'
                    ].map((check) => (
                      <div key={check} className="flex items-center gap-3 text-slate-700 font-semibold">
                        <div className="w-5 h-5 rounded-full bg-security/20 text-security flex items-center justify-center text-[10px]">✓</div>
                        {check}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex-1 bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
                  <h4 className="text-xs uppercase font-black tracking-widest text-slate-400 mb-6">Rubros que confían en nosotros</h4>
                  <div className="grid grid-cols-1 gap-4">
                    {[
                      'Energía, Gas e Ingeniería Química',
                      'Salud y Productos Médicos',
                      'Industria Metalúrgica y Manufactura',
                      'Brokers de Seguros y Administración',
                      'Importación de Tecnología y Logística',
                      'Industria Vitivinícola Internacional'
                    ].map((rubro) => (
                      <div key={rubro} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
                        <div className="w-2 h-2 rounded-full bg-accent"></div>
                        <span className="text-sm font-bold text-primary">{rubro}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-12 text-center">
                <a href="https://wa.me/541126552124" className="btn-primary inline-flex text-lg px-10 py-5">
                  <MessageCircle size={24} className="mr-2" /> Hablemos de tu proyecto
                </a>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Noticias Section - Carrusel */}
      <Section
        title="Actualidad Tecnológica"
        subtitle="Monitoreo constante de amenazas y avances. Información crítica para la toma de decisiones."
        id="noticias"
        className="bg-slate-50"
      >
        <div className="relative max-w-4xl mx-auto">
          {/* Carrusel Container */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentNewsIndex * 100}%)` }}
            >
              {news.map((item) => (
                <div key={item.id} className="w-full flex-shrink-0 px-4">
                  <div className="news-card group h-full">
                    <div className="flex justify-between items-start mb-4">
                      <span className="news-tag flex items-center gap-1">
                        <Clock size={10} /> {item.date}
                      </span>
                      <span className="news-tag flex items-center gap-1">
                        <Globe size={10} /> {item.language}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold mb-3 text-primary group-hover:text-accent transition-colors leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-sm font-bold text-slate-500 mb-3 italic">
                      {item.summary}
                    </p>
                    <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                      {item.content}
                    </p>
                    <div className="mt-auto">
                      <a
                        href={item.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary hover:text-accent transition-colors"
                      >
                        Leer noticia completa <ExternalLink size={14} />
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={prevNews}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all z-10"
            aria-label="Noticia anterior"
          >
            <ChevronRight size={24} className="rotate-180" />
          </button>
          <button
            onClick={nextNews}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all z-10"
            aria-label="Siguiente noticia"
          >
            <ChevronRight size={24} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-8">
            {news.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentNewsIndex(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentNewsIndex
                    ? 'bg-primary w-8'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Ir a noticia ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Botón Ver Más */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setIsNewsModalOpen(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Newspaper size={20} />
            Ver más noticias
          </button>
        </div>

        <div className="mt-8 flex items-center justify-center gap-4 text-[10px] uppercase font-black tracking-[0.2em] text-slate-400">
          <div className="h-px w-12 bg-slate-200"></div>
          Actualización diaria vía sistema automatizado
          <div className="h-px w-12 bg-slate-200"></div>
        </div>
      </Section>

      {/* Popup Modal de Noticias */}
      {isNewsModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="bg-primary text-white p-6 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold">Noticias de los últimos 5 días</h3>
                <p className="text-slate-300 text-sm mt-1">Archivo de actualidad tecnológica</p>
              </div>
              <button
                onClick={() => setIsNewsModalOpen(false)}
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Cerrar"
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {newsArchive.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {newsArchive.map((item) => (
                    <div key={item.id} className="news-card group">
                      <div className="flex justify-between items-start mb-3">
                        <span className="news-tag flex items-center gap-1">
                          <Clock size={10} /> {item.date}
                        </span>
                        <span className="news-tag flex items-center gap-1">
                          <Globe size={10} /> {item.language}
                        </span>
                      </div>
                      <h4 className="text-lg font-bold mb-2 text-primary group-hover:text-accent transition-colors leading-tight">
                        {item.title}
                      </h4>
                      <p className="text-sm font-bold text-slate-500 mb-2 italic">
                        {item.summary}
                      </p>
                      <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                        {item.content}
                      </p>
                      <a
                        href={item.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary hover:text-accent transition-colors"
                      >
                        Leer noticia completa <ExternalLink size={14} />
                      </a>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Cargando archivo de noticias...</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-slate-50 p-4 border-t border-slate-200 text-center">
              <button
                onClick={() => setIsNewsModalOpen(false)}
                className="text-sm font-semibold text-primary hover:text-accent transition-colors"
              >
                Cerrar ventana
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contacto Section */}
      <Section title="Contacto Directo" id="contacto" className="bg-primary text-white">
        <div className="grid md:grid-cols-2 gap-16">
          <div className="space-y-10">
            <div className="flex items-start gap-6 group">
              <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <Phone size={28} />
              </div>
              <div>
                <div className="text-xs uppercase font-black tracking-widest text-slate-500 mb-2">Teléfonos Directos</div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold">+54 11 2655-2124</span>
                    <a href="https://wa.me/541126552124" className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors shadow-lg" title="WhatsApp Luis">
                      <MessageCircle size={16} fill="currentColor" />
                    </a>
                    <span className="text-slate-500 font-medium text-sm">(Luis)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold">+54 11 2655-2123</span>
                    <a href="https://wa.me/541126552123" className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white hover:bg-green-600 transition-colors shadow-lg" title="WhatsApp Brando">
                      <MessageCircle size={16} fill="currentColor" />
                    </a>
                    <span className="text-slate-500 font-medium text-sm">(Brando)</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-start gap-6 group">
              <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <Mail size={28} />
              </div>
              <div>
                <div className="text-xs uppercase font-black tracking-widest text-slate-500 mb-2">Consultas Técnicas</div>
                <div className="text-xl font-bold">tecnica@oggun.com.ar</div>
              </div>
            </div>
            
            <div className="flex items-start gap-6 group">
              <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center text-accent group-hover:bg-accent group-hover:text-white transition-all duration-300">
                <MapPin size={28} />
              </div>
              <div>
                <div className="text-xs uppercase font-black tracking-widest text-slate-500 mb-2">Sede CABA</div>
                <div className="text-xl font-bold">Zelarrayán 1756, CABA</div>
                <div className="text-sm text-slate-500 mt-1">Buenos Aires, Argentina</div>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800/50 p-8 rounded-3xl border border-slate-700">
            <h3 className="text-2xl font-bold mb-6">Información Institucional</h3>
            <div className="space-y-4 text-slate-400">
              <div className="flex justify-between border-b border-slate-700 pb-4">
                <span className="font-bold uppercase text-xs tracking-widest">Razón Social</span>
                <span className="text-white font-semibold">LUIS FRANCISCO LUDOVICO</span>
              </div>
              <div className="flex justify-between border-b border-slate-700 pb-4">
                <span className="font-bold uppercase text-xs tracking-widest">CUIT</span>
                <span className="text-white font-semibold">20-17293714-5</span>
              </div>
              <div className="flex justify-between border-b border-slate-700 pb-4">
                <span className="font-bold uppercase text-xs tracking-widest">Especialidad</span>
                <span className="text-white font-semibold">Consultoría MSP & Redes</span>
              </div>
            </div>
            <div className="mt-10">
              <a href="https://wa.me/541126552124" className="btn-primary w-full justify-center py-4">
                <MessageCircle size={20} /> Iniciar Conversación
              </a>
            </div>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="py-10 bg-primary border-t border-slate-800 text-center">
        <div className="container mx-auto px-4">
          <Logo className="text-white justify-center" />
          <p className="text-slate-500 text-xs mt-4 uppercase tracking-[0.3em]">
            © {new Date().getFullYear()} OGGUN ITS - Todos los derechos reservados
          </p>
        </div>
      </footer>
    </div>
  );
}
